/**
 * Whether continuous decorative motion is allowed right now.
 *
 * The port of Android's `Motion.shouldAnimate` (ui/theme/Motion.kt). Same
 * reason for existing: this app spends its life polling on a field device with
 * nobody watching, so an animation that runs forever is a battery cost before
 * it is a design choice — and on a WebClip that battery is the whole product,
 * because the app stops measuring the moment the phone sleeps (SPEC §9).
 *
 * Two differences from Android, both forced by the platform:
 *
 * 1. Android reads `Settings.Global.ANIMATOR_DURATION_SCALE`, which is where
 *    both the accessibility toggle and Developer Options write. The web
 *    equivalent is `prefers-reduced-motion`, fed on iOS by
 *    Settings → Accessibility → Motion → Reduce Motion. Same signal, same
 *    meaning.
 *
 * 2. **Power-save mode is not readable from Safari, and there is no way to
 *    make it readable.** Android refuses to animate in power-save; here Low
 *    Power Mode passes completely unobserved. That is an accepted gap, not an
 *    oversight — it is also why `ios_setup_lowpower` tells the operator to turn
 *    Low Power Mode off by hand, since the app can neither read it nor react to
 *    it. Nothing here should pretend otherwise by guessing from battery level.
 *
 * CSS handles this on its own for anything declarative: every transition and
 * keyframe in components.css is already inside a
 * `@media (prefers-reduced-motion: reduce)` guard. This function exists for the
 * two things CSS cannot gate — the `<canvas>` render loops in AppBackground and
 * GlobeHero, which have to decide whether to schedule frames at all. Creating
 * an animation loop and setting its duration to zero still schedules frames;
 * the point is to never start one.
 */
export function prefersReducedMotion(): boolean {
  // Guarded rather than assumed. This module is imported by components that a
  // node-environment test may pull in transitively (vite.config.ts pins
  // `test.environment: 'node'`), and a decorative effect is never worth a
  // `matchMedia is not defined` that takes the whole render down.
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true;
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    // An unreadable preference resolves to "hold still", matching Motion.kt's
    // NaN handling: being wrong that way costs an effect, being wrong the other
    // way costs battery on a phone that is supposed to still be reporting in
    // eight hours.
    return true;
  }
}
